import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useParams, Link } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

const BRAND_CONFIG = {
  portrait: {
    hero: {
      heading: "UK Portrait Photography & Creative Headshots",
      subheadline:
        "Capturing your authentic self with editorial precision. We create a relaxed, intentional environment so your true personality comes through effortlessly.",
      cta: { label: "Book a Portrait Session", href: "#booking" },
      secondaryCta: { label: "View the Gallery", href: "#gallery" },
    },
    about: {
      heading: "Premium UK Portrait Photography",
      body: `Stepping in front of a camera can often feel intimidating. We eliminate that pressure by fostering a genuinely relaxed atmosphere, guiding you gently rather than forcing stiff, unnatural poses.

Whether you are an executive requiring a commanding headshot in Manchester, an artist building a creative portfolio, or a family wanting to preserve a specific chapter of life, the process is highly collaborative. The final images are clean, intentional, and deeply personal.`,
      deliverables: [
        "Professional corporate headshots and team profiles",
        "Editorial and creative branding portraits for entrepreneurs",
        "Timeless individual and family documentation",
        "Flexible execution in our studio or at your preferred UK location",
        "Advanced, natural-looking retouching to maintain authenticity",
      ],
    },
    booking: {
      heading: "Schedule Your UK Session",
      body: "Connect with us to discuss your vision, explore wardrobe concepts, and secure your preferred studio or location date.",
      fields: [
        { name: "fullName", label: "Full Name", type: "text" },
        { name: "email", label: "Email Address", type: "email" },
        { name: "phone", label: "UK Phone Number (+44)", type: "tel" },
        {
          name: "portraitType",
          label: "Portrait Type",
          type: "select",
          options: ["Corporate", "Creative / Brand", "Family", "Individual"],
        },
        {
          name: "location",
          label: "Preferred Location",
          type: "select",
          options: ["Studio", "On-Location"],
        },
        {
          name: "goal",
          label: "Describe the goal of your session",
          type: "textarea",
        },
      ],
      cta: "Submit Enquiry",
    },
  },

  wedding: {
    hero: {
      heading: "UK Wedding Photography and Cinematography",
      subheadline:
        "Documenting authentic love stories across Manchester and worldwide.",
      cta: { label: "Check Our Availability", href: "#booking" },
    },
    about: {
      heading: "Capturing a Lifetime: UK Wedding Photography & Film",
      body: `Your celebration is not a photoshoot. It is a living, breathing milestone. Our documentation process respects the natural rhythm of your day, capturing the loud celebrations alongside the quiet moments in between.

By remaining completely unobtrusive, we preserve the authentic atmosphere of your chosen venue and the genuine joy of your guests. We deliver a timeless collection of photographs and film that serve as an honest, beautiful record of your lifetime commitment.`,
      deliverables: [
        "Comprehensive coverage (single or multi-day) built around your timeline, not ours.",
        "Unobtrusive photojournalism paired with fast, elegant, editorial-style portraits.",
        "A synchronized, in-house photo and film team. Zero vendor clashing.",
        "A curated, high-resolution digital archive of your event.",
      ],
    },
    testimonial: {
      quote:
        "They didn't just take pictures; they captured the exact feeling of the room. Looking at the gallery is like living the day all over again.",
      author: "Client Name, Manchester",
    },
    booking: {
      heading: "Secure Your Date",
      body: "We strictly limit our annual wedding commissions. This is the only way we guarantee our complete creative focus remains entirely on your celebration. Fill out the details below to check our UK availability.",
      fields: [
        { name: "names", label: "Names", type: "text" },
        { name: "weddingDate", label: "Target Wedding Date", type: "date" },
        { name: "venue", label: "Venue / Location", type: "text" },
        {
          name: "vision",
          label: "Tell us about your vision for the day",
          type: "textarea",
        },
      ],
      cta: "Submit Enquiry",
    },
  },

  commercial: {
    hero: {
      heading: "UK Commercial & Brand Photography",
      subheadline:
        "Elevating corporate identities and creative campaigns. We produce high-impact visual assets that command attention and communicate your brand's core narrative across every platform.",
      cta: { label: "Request a Commercial Quote", href: "#booking" },
      secondaryCta: { label: "View Campaigns", href: "#gallery" },
    },
    about: {
      heading: "Manchester Commercial Photography & Film",
      body: `A strong visual identity is an active business asset. Not an afterthought.

We partner with UK brands, creative agencies, and corporate institutions to translate complex messaging into striking imagery.

Whether we are executing a nationwide advertising campaign, building an e-commerce catalogue, or documenting executive leadership in Manchester, our precise production process resonates deeply with your target market, establishes instant market authority, and drives tangible commercial engagement.`,
      deliverables: [
        "Executive portraiture and dynamic team headshots",
        "Lifestyle, editorial, and product campaigns",
        "Comprehensive corporate event and brand activation coverage",
        "End-to-end media production, including cinematography and creative direction",
        "Full commercial licensing and rapid turnaround times for fast-paced marketing needs",
      ],
    },
    trustedBy: {
      heading: "Trusted by Industry Leaders",
      body: "Our team delivers compelling visual narratives for major national and global institutions. We understand the strict demands of corporate procurement, rigid agency timelines, and the need for absolute reliability on set.",
      logos: [
        "Stanbic IBTC Bank",
        "Haier Thermocool",
        "PENCOM",
        "Ekiti State Government",
      ],
    },
    booking: {
      heading: "Initiate a Project",
      body: "",
      fields: [
        { name: "fullName", label: "Full Name", type: "text" },
        { name: "company", label: "Company / Agency Name", type: "text" },
        { name: "email", label: "Work Email Address", type: "email" },
        { name: "phone", label: "UK Phone Number (+44)", type: "tel" },
        {
          name: "scope",
          label: "Project Scope (Brief description of the campaign or event)",
          type: "textarea",
        },
        {
          name: "timeline",
          label: "Estimated Timeline / Delivery Deadline",
          type: "text",
        },
      ],
      cta: "Submit Project Brief",
    },
  },

  events: {
    hero: {
      heading: "UK Event Photography and Cinematography",
      subheadline:
        "Documenting the energy, atmosphere, and defining moments of concerts, corporate events, and live experiences across Manchester and the UK.",
      cta: { label: "Check Our Availability", href: "#booking" },
    },
    about: {
      heading: "When the Moment Passes, the Image Remains: UK Event Photography & Film",
      body: `Live events are unrepeatable. The energy of a crowd, the weight of a keynote, the electricity of a performance, these moments exist for seconds, and then they are gone. Our role is to make certain they are not.

We work invisibly within your event, anticipating the decisive moments before they happen. From the opening act to the final applause, we move through your venue without disrupting the atmosphere you have worked so hard to create. Every frame is composed with intention. Every edit is crafted to reflect the true character of what took place.

Whether you are producing a headline concert, a brand summit, an awards ceremony, or a private cultural gathering, we deliver a body of work that gives your event a lasting identity.`,
      deliverables: [
        "Comprehensive event coverage built around your full programme — from setup through to close.",
        "Fast-moving, unobtrusive photojournalism capturing crowd energy, performer presence, and key speaker moments.",
        "Editorial-grade post-production: clean, vivid, true to the atmosphere of the room.",
        "Optional in-house cinematography for highlight reels, social content, or full event documentation.",
        "A curated, high-resolution digital archive, delivered promptly for press, marketing, and archival use.",
      ],
    },
    testimonial: {
      quote:
        "The gallery captured exactly what it felt like to be in that room — the crowd, the light, the energy. We've used the images across every channel, and they still stop people mid-scroll.",
      author: "Olivia, Manchester",
    },
    booking: {
      heading: "Let's Document Your Next Event",
      body: "We work with a carefully selected number of events each year to ensure every client receives our full creative attention. Whether your event is two weeks away or twelve months out, the best time to secure your documentation team is now. Fill out the details below to check our availability and begin the conversation.",
      fields: [
        { name: "leadName", label: "Lead / Organisation Name", type: "text" },
        {
          name: "eventType",
          label: "Event Type",
          type: "select",
          options: ["Concert", "Corporate", "Awards", "Brand Activation", "Other"],
        },
        { name: "eventDate", label: "Event Date(s)", type: "date" },
        { name: "venue", label: "Venue / Location", type: "text" },
        { name: "attendance", label: "Expected Attendance", type: "text" },
        {
          name: "details",
          label: "Tell us about the event and what you need from your documentation",
          type: "textarea",
        },
      ],
      cta: "Submit Enquiry",
    },
  },

  documentary: {
    hero: {
      heading: "UK Documentary Photography & Photojournalism",
      subheadline:
        "Uncover the profound truth within real-world narratives. We embed ourselves in your cause, community, or culture to create visual essays that speak volumes.",
      cta: { label: "Commission a Project", href: "#booking" },
    },
    about: {
      heading: "Contextualising Truth: UK Documentary Photography & Film",
      body: `We embed ourselves deeply within the environments we photograph to understand the core of the story long before we ever raise a camera. This immersive method allows us to document raw, unmanipulated realities for editorial publications, humanitarian organisations, and private clients across the UK and internationally.

Bypassing surface-level aesthetics, the resulting imagery delivers a truthful narrative engineered to drive awareness and foster real human connection.`,
      deliverables: [
        "Long-term embedded photojournalism and complete visual essays",
        "Short-form documentary filmmaking and cinematic interviews",
        "Humanitarian, cultural, and NGO field documentation",
        "Editorial portraiture of key subjects in their natural environments",
      ],
    },
    booking: {
      heading: "Commission Your Next Visual Story",
      body: "Meaningful stories take time and careful planning to tell accurately. Reach out to discuss the scope, location, and ultimate objective of your documentary assignment.",
      fields: [
        { name: "fullName", label: "Full Name", type: "text" },
        { name: "org", label: "Organisation / Publication Name", type: "text" },
        { name: "email", label: "Email Address", type: "email" },
        { name: "phone", label: "UK Phone Number (+44)", type: "tel" },
        {
          name: "subject",
          label: "Project Subject Matter / Core Narrative",
          type: "textarea",
        },
        {
          name: "locationDuration",
          label: "Target Location & Estimated Duration",
          type: "text",
        },
      ],
      cta: "Initiate Discussion",
    },
  },
};

const BrandHero = ({ config, brandName }) => {
  if (!config) return null;
  return (
    <div className="xl:px-44 px-6 pt-[100px] pb-16 border-b border-gray-100">
      <p className="uppercase tracking-[.25em] text-xs text-gray-400 mb-4">
        {brandName}
      </p>
      <h1 className="text-3xl md:text-4xl xl:text-5xl font-light leading-tight max-w-3xl mb-5">
        {config.heading}
      </h1>
      <p className="text-gray-500 text-base max-w-xl leading-relaxed mb-8">
        {config.subheadline}
      </p>
      <div className="flex flex-wrap gap-4">
        <a
          href={config.cta.href}
          className="bg-black text-white px-7 py-3 text-sm uppercase tracking-widest hover:bg-gray-800 transition"
        >
          {config.cta.label}
        </a>
        {config.secondaryCta && (
          <a
            href={config.secondaryCta.href}
            className="border border-black px-7 py-3 text-sm uppercase tracking-widest hover:bg-black hover:text-white transition"
          >
            {config.secondaryCta.label}
          </a>
        )}
      </div>
    </div>
  );
};

const AboutSection = ({ config }) => {
  if (!config) return null;
  return (
    <div className="xl:px-44 px-6 py-20 border-b border-gray-100">
      <h2 className="text-2xl md:text-3xl font-light mb-8 max-w-2xl">
        {config.heading}
      </h2>
      <div className="grid xl:grid-cols-2 gap-12 items-start">
        <p className="text-gray-600 leading-relaxed whitespace-pre-line text-sm md:text-base">
          {config.body}
        </p>
        <div>
          <p className="uppercase tracking-widest text-xs text-gray-400 mb-4">
            The Deliverables
          </p>
          <ul className="space-y-3">
            {config.deliverables.map((item, i) => (
              <li key={i} className="flex gap-3 text-sm text-gray-700">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-black shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const Testimonial = ({ config }) => {
  if (!config) return null;
  return (
    <div className="xl:px-44 px-6 py-20 bg-gray-50 border-b border-gray-100">
      <blockquote className="max-w-2xl">
        <p className="text-xl md:text-2xl font-light italic text-gray-800 leading-relaxed mb-5">
          "{config.quote}"
        </p>
        <cite className="text-xs uppercase tracking-widest text-gray-400 not-italic">
          — {config.author}
        </cite>
      </blockquote>
    </div>
  );
};

const TrustedBy = ({ config }) => {
  if (!config) return null;
  return (
    <div className="xl:px-44 px-6 py-20 bg-gray-50 border-b border-gray-100">
      <h2 className="uppercase tracking-widest text-xs text-gray-400 mb-3">
        {config.heading}
      </h2>
      <p className="text-gray-600 text-sm max-w-xl leading-relaxed mb-10">
        {config.body}
      </p>
      <div className="flex flex-wrap gap-8 items-center">
        {config.logos.map((logo, i) => (
          <span
            key={i}
            className="text-sm uppercase tracking-widest text-gray-400 border border-gray-200 px-4 py-2"
          >
            {logo}
          </span>
        ))}
      </div>
    </div>
  );
};

const BookingForm = ({ config, portfolioType }) => {
  const [form, setForm] = useState({});
  const [status, setStatus] = useState(null); // null | "loading" | "success" | "error"

  if (!config) return null;

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/booking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ portfolioType, ...form }),
      });
      const data = await res.json();
      if (data.success) {
        setStatus("success");
        setForm({});
        e.target.reset();
      } else {
        setStatus("error");
      }
    } catch (err) {
      console.error("Booking submit error:", err);
      setStatus("error");
    }
  };

  return (
    <div id="booking" className="xl:px-44 px-6 py-20 border-b border-gray-100">
      <h2 className="text-2xl md:text-3xl font-light mb-3">{config.heading}</h2>
      {config.body && (
        <p className="text-gray-500 text-sm max-w-xl mb-10 leading-relaxed">
          {config.body}
        </p>
      )}
      <form onSubmit={handleSubmit} className="max-w-2xl space-y-5">
        {config.fields.map((field) => (
          <div key={field.name} className="flex flex-col gap-1.5">
            <label
              htmlFor={field.name}
              className="text-xs uppercase tracking-widest text-gray-500"
            >
              {field.label}
            </label>
            {field.type === "textarea" ? (
              <textarea
                id={field.name}
                name={field.name}
                rows={4}
                onChange={handleChange}
                className="border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-black transition resize-none"
              />
            ) : field.type === "select" ? (
              <select
                id={field.name}
                name={field.name}
                onChange={handleChange}
                className="border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-black transition bg-white"
              >
                <option value="">Select…</option>
                {field.options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id={field.name}
                name={field.name}
                type={field.type}
                onChange={handleChange}
                className="border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-black transition"
              />
            )}
          </div>
        ))}
        <button
          type="submit"
          disabled={status === "loading"}
          className="mt-4 bg-black text-white px-8 py-3 text-sm uppercase tracking-widest hover:bg-gray-800 transition disabled:opacity-50"
        >
          {status === "loading" ? "Submitting..." : config.cta}
        </button>
        {status === "success" && (
          <p className="text-sm text-green-600 uppercase tracking-widest">
            Enquiry received — we'll be in touch shortly.
          </p>
        )}
        {status === "error" && (
          <p className="text-sm text-red-500 uppercase tracking-widest">
            Something went wrong. Please try again.
          </p>
        )}
      </form>
    </div>
  );
};

const Portfolio = () => {
  const { brandName } = useParams();
  const [subBrands, setSubBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  const brand = BRAND_CONFIG[brandName?.toLowerCase()];

  useEffect(() => {
  const fetchUploads = async () => {
    try {
      setLoading(true);

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/uploads/${brandName}`
      );

      if (!res.ok) return;

      const uploads = await res.json();

      // 🔍 See raw API response
      console.log("RAW UPLOADS:", uploads);

      // 🔥 Group uploads by subbrand
      const grouped = uploads.reduce((acc, item) => {
        const subbrandId = item.subbrand._id;

        if (!acc[subbrandId]) {
          acc[subbrandId] = {
            _id: subbrandId,
            name: item.subbrand.name,
            slug: item.subbrand.slug,
            coverImage: item.image,
          };
        }

        return acc;
      }, {});

      // 🔍 See grouped result
      console.log("GROUPED SUBBRANDS:", Object.values(grouped));

      setSubBrands(Object.values(grouped));
    } catch (err) {
      console.error("FETCH ERROR:", err);
    } finally {
      setLoading(false);
    }
  };

  fetchUploads();
}, [brandName]);


  return (
    <>
      <Helmet>
        <title className="capitalize">{`${brandName} Portfolio | Glintz Photography`}</title>
      </Helmet>

      <Navbar />

      {/* Header */}
      {brand ? (
        <BrandHero config={brand.hero} brandName={brandName} />
      ) : (
        <div className="xl:px-44 px-6 pt-[100px]">
          <h1 className="uppercase tracking-[.25em] text-2xl">
            {brandName}
          </h1>
        </div>
      )}

      {/* CONTENT */}
      <div id="gallery" className="xl:px-20 px-6 mt-16 py-20">
        {loading ? (
          /* 🔄 Loader */
          <div className="flex justify-center items-center h-[300px]">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-black border-t-transparent"></div>
          </div>
        ) : (
          /* SUB-BRANDS GRID */
          <div className="grid xl:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-6">
            {subBrands.map((sub) => (
              <Link
                key={sub._id}
                to={`/portfolio/${brandName}/${sub.slug}`}
                className="group"
              >
                <div className="overflow-hidden">
                  <img
                    src={sub.coverImage}
                    alt={sub.name}
                    className="w-full h-[300px] object-cover group-hover:scale-105 transition"
                  />
                </div>

                <p className="mt-4 uppercase tracking-widest text-sm text-center">
                  {sub.name}
                </p>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* BRAND-SPECIFIC SECTIONS */}
      {brand && (
        <>
          <AboutSection config={brand.about} />
          <Testimonial config={brand.testimonial} />
          <TrustedBy config={brand.trustedBy} />
          <BookingForm config={brand.booking} portfolioType={brandName} />
        </>
      )}

      <Footer />
    </>
  );
};

export default Portfolio;